/**
 * 환경설정 — 기관 직인 · 회계단위(사업장) 직인
 * 로그인 기관코드 스코프만 관리 (기관 로고와 동일)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme as staticTheme } from 'styles/theme';
import { ActionButton } from 'styles/common.styles';
import { showAlert } from 'utils/toastBridge';
import {
  SEAL_MAX_BYTES,
  SEAL_STATUS_OPTIONS,
  createSealUid,
  loadSealBundle,
  saveSealBundle,
  listAcctUnitsForSeal,
  readSealImageFile,
  normalizeDefaultYn,
} from 'system/sealStore';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BlockTitle = styled.h3`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.md};
  font-weight: 700;
  color: ${(p) => p.theme.colors.text.primary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.tertiary};
  line-height: 1.45;
`;

const TenantHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.primaryLight};
  color: ${(p) => p.theme.colors.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 600;
  width: fit-content;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Label = styled.label`
  min-width: 72px;
  font-size: ${staticTheme.typography.fontSize.sm};
  color: ${(p) => p.theme.colors.text.secondary};
  text-align: right;
`;

const TextInput = styled.input`
  min-width: 160px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.input};
  color: ${(p) => p.theme.colors.text.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
`;

const Select = styled.select`
  min-width: 200px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.input};
  color: ${(p) => p.theme.colors.text.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SealCard = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.border)};
  border-radius: ${staticTheme.borderRadius.md};
  background: ${(p) => p.theme.colors.background.white};
  cursor: pointer;
`;

const Thumb = styled.div`
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border: 1px dashed ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${(p) => p.theme.colors.background.septenary || p.theme.colors.background.light};

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const CardMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.div`
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 600;
  color: ${(p) => p.theme.colors.text.primary};
`;

const CardSub = styled.div`
  font-size: ${staticTheme.typography.fontSize.xxs};
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const Badge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  background: ${(p) => p.theme.colors.primaryLight};
  color: ${(p) => p.theme.colors.primary};
`;

const Empty = styled.div`
  padding: 16px;
  text-align: center;
  font-size: ${staticTheme.typography.fontSize.sm};
  color: ${(p) => p.theme.colors.text.tertiary};
  border: 1px dashed ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.md};
`;

const HiddenFile = styled.input`
  display: none;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${staticTheme.typography.fontSize.sm};
  color: ${(p) => p.theme.colors.text.secondary};
  cursor: pointer;
  user-select: none;
`;

function emptyDraft(extra = {}) {
  return {
    sealUid: '',
    sealNm: '',
    sealImage: '',
    sealFileName: '',
    status: 'ACTIVE',
    defaultYn: 'N',
    validFrom: '',
    validTo: '',
    remark: '',
    ...extra,
  };
}

/**
 * @param {{ tenantCode?: string }} props
 */
const SealSettingsPanel = ({ tenantCode = '' }) => {
  const code = String(tenantCode || '').trim().toUpperCase();
  const fileRef = useRef(null);
  const [tenantSeals, setTenantSeals] = useState([]);
  const [unitSeals, setUnitSeals] = useState([]);
  const [selectedTenantSealUid, setSelectedTenantSealUid] = useState('');
  const [selectedUnitUid, setSelectedUnitUid] = useState('');
  const [selectedUnitSealUid, setSelectedUnitSealUid] = useState('');
  const [draft, setDraft] = useState(() => emptyDraft());
  const [editScope, setEditScope] = useState('tenant'); // tenant | unit

  const unitOptions = useMemo(() => listAcctUnitsForSeal(code), [code]);

  const reload = useCallback(() => {
    if (!code) {
      setTenantSeals([]);
      setUnitSeals([]);
      return;
    }
    const bundle = loadSealBundle(code);
    setTenantSeals(bundle.tenantSeals);
    setUnitSeals(bundle.unitSeals);
  }, [code]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!selectedUnitUid && unitOptions[0]?.value) {
      setSelectedUnitUid(unitOptions[0].value);
    }
  }, [unitOptions, selectedUnitUid]);

  const unitSealsFiltered = useMemo(
    () => unitSeals.filter((s) => String(s.acctUnitUid || '') === String(selectedUnitUid || '')),
    [unitSeals, selectedUnitUid]
  );

  const selectTenantSeal = useCallback((seal) => {
    setEditScope('tenant');
    setSelectedTenantSealUid(seal.sealUid);
    setDraft({ ...emptyDraft(), ...seal });
  }, []);

  const selectUnitSeal = useCallback((seal) => {
    setEditScope('unit');
    setSelectedUnitSealUid(seal.sealUid);
    setDraft({ ...emptyDraft(), ...seal });
  }, []);

  const startNewTenantSeal = useCallback(() => {
    setEditScope('tenant');
    setSelectedTenantSealUid('');
    setDraft(emptyDraft({ sealNm: '대표직인', defaultYn: tenantSeals.length ? 'N' : 'Y' }));
  }, [tenantSeals.length]);

  const startNewUnitSeal = useCallback(() => {
    if (!selectedUnitUid) {
      showAlert('회계단위를 먼저 선택하세요.');
      return;
    }
    const unit = unitOptions.find((u) => u.value === selectedUnitUid);
    setEditScope('unit');
    setSelectedUnitSealUid('');
    setDraft(
      emptyDraft({
        sealNm: `${unit?.name || '사업장'} 직인`,
        defaultYn: unitSealsFiltered.length ? 'N' : 'Y',
        acctUnitUid: selectedUnitUid,
        acctUnitCode: unit?.code || '',
      })
    );
  }, [selectedUnitUid, unitOptions, unitSealsFiltered.length]);

  const handlePickFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { dataUrl, fileName } = await readSealImageFile(file);
      setDraft((d) => ({ ...d, sealImage: dataUrl, sealFileName: fileName }));
    } catch (err) {
      showAlert(err?.message || '이미지를 읽지 못했습니다.');
    }
  }, []);

  const persistAll = useCallback(
    (nextTenant, nextUnit) => {
      const result = saveSealBundle(code, { tenantSeals: nextTenant, unitSeals: nextUnit });
      if (!result.ok) {
        showAlert(result.message || '저장에 실패했습니다.');
        return false;
      }
      setTenantSeals(nextTenant);
      setUnitSeals(nextUnit);
      return true;
    },
    [code]
  );

  const handleSaveDraft = useCallback(() => {
    if (!code) {
      showAlert('기관코드가 없습니다. 다시 로그인해 주세요.');
      return;
    }
    if (!draft.sealImage) {
      showAlert('직인 이미지를 선택하세요.');
      return;
    }
    const nm = String(draft.sealNm || '').trim() || '직인';
    const uid = draft.sealUid || createSealUid();
    const row = {
      ...draft,
      sealUid: uid,
      sealNm: nm,
      status: draft.status === 'REVOKED' ? 'REVOKED' : 'ACTIVE',
      defaultYn: draft.defaultYn === 'Y' ? 'Y' : 'N',
      updatedAt: new Date().toISOString(),
    };

    if (editScope === 'tenant') {
      let next = [...tenantSeals];
      const idx = next.findIndex((s) => s.sealUid === uid);
      if (idx >= 0) next[idx] = row;
      else next.push(row);
      next = normalizeDefaultYn(next, row.defaultYn === 'Y' ? uid : undefined);
      if (!persistAll(next, unitSeals)) return;
      setSelectedTenantSealUid(uid);
      setDraft(row);
      showAlert('기관 직인을 저장했습니다.');
      return;
    }

    if (!selectedUnitUid) {
      showAlert('회계단위를 선택하세요.');
      return;
    }
    const unit = unitOptions.find((u) => u.value === selectedUnitUid);
    row.acctUnitUid = selectedUnitUid;
    row.acctUnitCode = unit?.code || row.acctUnitCode || '';

    const others = unitSeals.filter((s) => String(s.acctUnitUid || '') !== selectedUnitUid);
    let mine = unitSeals.filter((s) => String(s.acctUnitUid || '') === selectedUnitUid);
    const idx = mine.findIndex((s) => s.sealUid === uid);
    if (idx >= 0) mine[idx] = row;
    else mine.push(row);
    mine = normalizeDefaultYn(mine, row.defaultYn === 'Y' ? uid : undefined);
    if (!persistAll(tenantSeals, [...others, ...mine])) return;
    setSelectedUnitSealUid(uid);
    setDraft(row);
    showAlert('회계단위 직인을 저장했습니다.');
  }, [
    code,
    draft,
    editScope,
    tenantSeals,
    unitSeals,
    selectedUnitUid,
    unitOptions,
    persistAll,
  ]);

  const handleDeleteDraft = useCallback(() => {
    const uid = draft.sealUid;
    if (!uid) {
      setDraft(emptyDraft());
      return;
    }
    if (editScope === 'tenant') {
      const next = normalizeDefaultYn(tenantSeals.filter((s) => s.sealUid !== uid));
      if (!persistAll(next, unitSeals)) return;
      setSelectedTenantSealUid('');
      setDraft(emptyDraft());
      showAlert('기관 직인을 삭제했습니다.');
      return;
    }
    const next = unitSeals.filter((s) => s.sealUid !== uid);
    const mine = normalizeDefaultYn(
      next.filter((s) => String(s.acctUnitUid || '') === selectedUnitUid)
    );
    const others = next.filter((s) => String(s.acctUnitUid || '') !== selectedUnitUid);
    if (!persistAll(tenantSeals, [...others, ...mine])) return;
    setSelectedUnitSealUid('');
    setDraft(emptyDraft({ acctUnitUid: selectedUnitUid }));
    showAlert('회계단위 직인을 삭제했습니다.');
  }, [draft.sealUid, editScope, tenantSeals, unitSeals, selectedUnitUid, persistAll]);

  return (
    <Wrap>
      <Hint>
        로그인한 기관의 직인만 등록합니다. 기관(법인) 직인과 회계단위(사업장) 직인을 구분해 관리합니다.
        이미지 최대 약 {Math.floor(SEAL_MAX_BYTES / 1024)}KB. 기관등록·회계단위등록 화면의 직인 UI는
        이 환경설정으로 이관되었습니다.
      </Hint>
      <TenantHint>기관 {code || '—'}</TenantHint>

      <Block>
        <BlockTitle>기관 직인</BlockTitle>
        <Hint>법인 대표 직인 등 기관 공통 날인. 기본직인 1건을 지정할 수 있습니다.</Hint>
        <CardList>
          {tenantSeals.length === 0 ? (
            <Empty>등록된 기관 직인이 없습니다.</Empty>
          ) : (
            tenantSeals.map((s) => (
              <SealCard
                key={s.sealUid}
                type="button"
                $active={editScope === 'tenant' && selectedTenantSealUid === s.sealUid}
                onClick={() => selectTenantSeal(s)}
              >
                <Thumb>{s.sealImage ? <img src={s.sealImage} alt={s.sealNm} /> : '—'}</Thumb>
                <CardMeta>
                  <CardTitle>
                    {s.sealNm || '직인'}
                    {s.defaultYn === 'Y' ? <Badge>기본</Badge> : null}
                  </CardTitle>
                  <CardSub>
                    {s.status === 'REVOKED' ? '폐기' : '사용'}
                    {s.sealFileName ? ` · ${s.sealFileName}` : ''}
                  </CardSub>
                </CardMeta>
              </SealCard>
            ))
          )}
        </CardList>
        <Row>
          <ActionButton type="button" onClick={startNewTenantSeal} disabled={!code}>
            기관 직인 추가
          </ActionButton>
        </Row>
      </Block>

      <Block>
        <BlockTitle>회계단위(사업장) 직인</BlockTitle>
        <Hint>본점·지점·센터 등 회계단위별로 직인을 등록합니다. 회계단위등록 마스터가 필요합니다.</Hint>
        <Row>
          <Label htmlFor="seal-acct-unit">회계단위</Label>
          <Select
            id="seal-acct-unit"
            value={selectedUnitUid}
            onChange={(e) => {
              setSelectedUnitUid(e.target.value);
              setSelectedUnitSealUid('');
              setDraft(emptyDraft({ acctUnitUid: e.target.value }));
              setEditScope('unit');
            }}
            disabled={!unitOptions.length}
          >
            {unitOptions.length === 0 ? (
              <option value="">등록된 회계단위 없음</option>
            ) : (
              unitOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))
            )}
          </Select>
          <ActionButton
            type="button"
            onClick={startNewUnitSeal}
            disabled={!code || !selectedUnitUid}
          >
            단위 직인 추가
          </ActionButton>
        </Row>
        <CardList>
          {!selectedUnitUid ? (
            <Empty>회계단위를 선택하세요.</Empty>
          ) : unitSealsFiltered.length === 0 ? (
            <Empty>이 회계단위에 등록된 직인이 없습니다.</Empty>
          ) : (
            unitSealsFiltered.map((s) => (
              <SealCard
                key={s.sealUid}
                $active={editScope === 'unit' && selectedUnitSealUid === s.sealUid}
                onClick={() => selectUnitSeal(s)}
              >
                <Thumb>{s.sealImage ? <img src={s.sealImage} alt={s.sealNm} /> : '—'}</Thumb>
                <CardMeta>
                  <CardTitle>
                    {s.sealNm || '직인'}
                    {s.defaultYn === 'Y' ? <Badge>기본</Badge> : null}
                  </CardTitle>
                  <CardSub>
                    {s.status === 'REVOKED' ? '폐기' : '사용'}
                    {s.sealFileName ? ` · ${s.sealFileName}` : ''}
                  </CardSub>
                </CardMeta>
              </SealCard>
            ))
          )}
        </CardList>
      </Block>

      <Block>
        <BlockTitle>
          직인 편집
          {editScope === 'unit' ? ' (회계단위)' : ' (기관)'}
        </BlockTitle>
        <Row>
          <Label htmlFor="seal-nm">명칭</Label>
          <TextInput
            id="seal-nm"
            value={draft.sealNm}
            onChange={(e) => setDraft((d) => ({ ...d, sealNm: e.target.value }))}
            placeholder="예: 대표직인"
          />
          <Label htmlFor="seal-status">상태</Label>
          <Select
            id="seal-status"
            value={draft.status}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
          >
            {SEAL_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
          <ToggleLabel>
            <input
              type="checkbox"
              checked={draft.defaultYn === 'Y'}
              onChange={(e) =>
                setDraft((d) => ({ ...d, defaultYn: e.target.checked ? 'Y' : 'N' }))
              }
            />
            기본직인
          </ToggleLabel>
        </Row>
        <Row>
          <Label>미리보기</Label>
          <Thumb style={{ width: 96, height: 96 }}>
            {draft.sealImage ? (
              <img src={draft.sealImage} alt={draft.sealNm || '직인'} />
            ) : (
              <span style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', padding: 4 }}>
                이미지 없음
              </span>
            )}
          </Thumb>
          <HiddenFile ref={fileRef} type="file" accept="image/*" onChange={handlePickFile} />
          <ActionButton type="button" onClick={() => fileRef.current?.click()} disabled={!code}>
            이미지 선택
          </ActionButton>
          {draft.sealFileName ? <CardSub>{draft.sealFileName}</CardSub> : null}
        </Row>
        <Row>
          <Label />
          <ActionButton type="button" $variant="primary" onClick={handleSaveDraft} disabled={!code}>
            직인 저장
          </ActionButton>
          <ActionButton
            type="button"
            onClick={handleDeleteDraft}
            disabled={!code || !draft.sealUid}
          >
            삭제
          </ActionButton>
        </Row>
      </Block>
    </Wrap>
  );
};

export default SealSettingsPanel;
